import React from 'react';
import { useTranslation } from '@/i18n';
import { Selector } from '@/components/ui/inputs/selector';
import { PrimaryButton } from '@/components/ui/buttons';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import { TransferPanel } from '@/components/ui/panels/TransferPanel';
import type { PanelSideConfig } from '@/components/ui/panels/TransferPanel';
import { PermissionGuard } from '@/components/layout';
import { useMobileAppPermissions } from '@/hooks/authorization/useMobileAppPermissions';
import { useMobileAppEmployeeAssignment } from '@/hooks/authorization/useMobileAppEmployeeAssignment';
import { useMobileAppPermissionsExport } from '@/hooks/authorization/useMobileAppPermissionsExport';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { SECTIONS } from '@/types/sections';
import type { AppMobileProfileEmployee } from '@/api/domains/employee/appMobileProfileEmployees';

// El movimiento se realiza con los botones Agregar/Quitar. No hay movimiento por doble clic
// ni scroll infinito (los empleados se cargan por completo de una sola vez).
const noop = (): void => {};
const noopRef: React.RefCallback<HTMLDivElement> = () => {};

interface PanelSelection {
  selectedIds: Set<number>;
  onToggleSelect: (employeeId: number) => void;
  onSelectAll: () => void;
  isAllSelected: boolean;
  isIndeterminate: boolean;
}

/**
 * Pestaña "Permisos APP Móvil".
 * Permite seleccionar el perfil a gestionar, seleccionar empleados en cada panel y
 * moverlos entre "Sin asignar" y "Asignados" con los botones Agregar/Quitar.
 */
export const MobileAppPermissionsForm: React.FC = () => {
  const { t: translate } = useTranslation();
  const { canSection } = usePagePermissions();
  const canEdit = canSection(SECTIONS.PERMISOS_APP_MOVIL, 'edit');
  const canView = canSection(SECTIONS.PERMISOS_APP_MOVIL, 'view');
  const { profileOptions, selectedProfileId, onProfileChange } = useMobileAppPermissions();
  const { exportAssignments, isExporting } = useMobileAppPermissionsExport(selectedProfileId);
  const {
    filteredUnassigned,
    unassigned,
    unassignedSearchText,
    onUnassignedSearch,
    filteredAssigned,
    assigned,
    assignedSearchText,
    onAssignedSearch,
    selectedUnassignedIds,
    onToggleUnassigned,
    onSelectAllUnassigned,
    isAllUnassignedSelected,
    isUnassignedIndeterminate,
    selectedAssignedIds,
    onToggleAssigned,
    onSelectAllAssigned,
    isAllAssignedSelected,
    isAssignedIndeterminate,
    onAdd,
    onRemove,
    isAddDisabled,
    isRemoveDisabled,
    onSave,
    onCancel,
    isSaving,
    hasPendingChanges,
    isLoading,
  } = useMobileAppEmployeeAssignment(selectedProfileId);

  const buildPanelConfig = (
    title: string,
    items: AppMobileProfileEmployee[],
    allItems: AppMobileProfileEmployee[],
    searchValue: string,
    onSearch: (text: string) => void,
    selection: PanelSelection,
    testId: string,
  ): PanelSideConfig<AppMobileProfileEmployee> => ({
    title,
    items,
    allItems,
    isLoading,
    totalRecords: allItems.length,
    searchValue,
    onSearch,
    searchPlaceholder: translate('authorization:mobileAppPermissions.searchPlaceholder'),
    selectedIds: selection.selectedIds,
    onToggleSelect: selection.onToggleSelect,
    onSelectAll: selection.onSelectAll,
    isAllSelected: selection.isAllSelected,
    isIndeterminate: selection.isIndeterminate,
    onDoubleClick: noop,
    sentinelRef: noopRef,
    scrollContainerRef: noopRef,
    footerText: translate('authorization:mobileAppPermissions.footerCount', { count: items.length }),
    'data-testid': testId,
  });

  return (
    <div className="flex flex-col gap-6" data-testid="mobile-app-permissions-form">
      <div className="flex items-end justify-between gap-4">
        <div className="w-96">
          <Selector
            label={translate('authorization:mobileAppPermissions.profileLabel')}
            options={profileOptions}
            value={selectedProfileId ? String(selectedProfileId) : undefined}
            onChange={(value) => onProfileChange(value ? Number(value) : null)}
            disabled={isSaving}
            data-testid="mobile-app-permissions-profile-selector"
          />
        </div>

        <PermissionGuard action="view" fallback="hidden" denied={!canView}>
          <ExportDropdown
            onExport={(format) => { void exportAssignments(format); }}
            availableFormats={['EXCEL']}
            disabled={selectedProfileId === null || isExporting}
            isLoading={isExporting}
            data-testid="mobile-app-permissions-download-button"
          />
        </PermissionGuard>
      </div>

      <TransferPanel<AppMobileProfileEmployee>
        keyExtractor={(employee) => employee.employeeId}
        renderTitle={(employee) => employee.fullName}
        renderSubtitle={(employee) => employee.rut}
        leftPanel={buildPanelConfig(
          translate('authorization:mobileAppPermissions.unassigned'),
          filteredUnassigned,
          unassigned,
          unassignedSearchText,
          onUnassignedSearch,
          {
            selectedIds: selectedUnassignedIds,
            onToggleSelect: onToggleUnassigned,
            onSelectAll: onSelectAllUnassigned,
            isAllSelected: isAllUnassignedSelected,
            isIndeterminate: isUnassignedIndeterminate,
          },
          'mobile-app-permissions-unassigned',
        )}
        rightPanel={buildPanelConfig(
          translate('authorization:mobileAppPermissions.assigned'),
          filteredAssigned,
          assigned,
          assignedSearchText,
          onAssignedSearch,
          {
            selectedIds: selectedAssignedIds,
            onToggleSelect: onToggleAssigned,
            onSelectAll: onSelectAllAssigned,
            isAllSelected: isAllAssignedSelected,
            isIndeterminate: isAssignedIndeterminate,
          },
          'mobile-app-permissions-assigned',
        )}
        onAdd={onAdd}
        onRemove={onRemove}
        isAddDisabled={isAddDisabled || isSaving}
        isRemoveDisabled={isRemoveDisabled || isSaving}
        hideActions={!canEdit}
        data-testid="mobile-app-permissions-transfer"
      />

      <PermissionGuard action="edit" fallback="hidden" denied={!canEdit}>
        <div className="flex justify-center gap-3 pt-2">
          <PrimaryButton
            variant="outline"
            onClick={onCancel}
            disabled={!hasPendingChanges || isSaving}
            data-testid="mobile-app-permissions-cancel-button"
          >
            {translate('authorization:mobileAppPermissions.cancel')}
          </PrimaryButton>
          <PrimaryButton
            onClick={() => { void onSave(); }}
            isLoading={isSaving}
            disabled={!hasPendingChanges || isSaving}
            data-testid="mobile-app-permissions-save-button"
          >
            {translate('authorization:mobileAppPermissions.saveChanges')}
          </PrimaryButton>
        </div>
      </PermissionGuard>
    </div>
  );
};

export default MobileAppPermissionsForm;
